import LegalPageLayout from '@/components/legal/LegalPageLayout'
import SEO from '@/components/layout/SEO'

export default function PrivacyPage() {
  return (
    <LegalPageLayout title="Privacy Policy" lastUpdated="September 17, 2026">
      <SEO
        title="Privacy Policy"
        description="Learn how Still Waters Academy protects your personal data, ensures COPPA compliance for child learners, and safeguards family education records."
      />
      <section>
        <p>
          Welcome to Still Waters Academy. Your privacy and the safeguarding of your family's data is fundamental to our mission.
          As an AI-assisted educational academy and mentorship platform serving students of all ages, including children and adolescents, we adhere to the highest standards of data security, integrity, and regulatory compliance.
        </p>
        <p>
          This Privacy Policy describes our practices regarding the collection, use, and disclosure of information when you use our website, educational applications, student dashboards, and AI tutoring services (collectively, the <strong>"Service"</strong>).
        </p>
        <blockquote>
          Still Waters Academy is operated by <strong>Still Waters LLC</strong>, a limited liability company registered in the State of Wyoming, USA, doing business as <strong>Still Waters Academy</strong> (<strong>"Still Waters"</strong>, <strong>"we"</strong>, <strong>"us"</strong>, or <strong>"our"</strong>).
        </blockquote>
      </section>

      <section>
        <h2>1. Information We Collect</h2>
        <p>
          We collect information strictly necessary to provide a personalized, secure, and rigorous educational experience:
        </p>
        <ul>
          <li><strong>Parent & Account Data:</strong> Name, verified email address, billing information, and facility identifier when a parent or guardian establishes a family education hub.</li>
          <li><strong>Student Profile Data:</strong> Student name or display moniker, age, and assigned curriculum track. For students under 18, accounts are created exclusively by the parent or verified institution.</li>
          <li><strong>Academic Progress & Learning Records:</strong> Lesson logs, unit completions, quiz scores, mastery assessments, strengths, and areas for improvement.</li>
          <li><strong>Lesson & Tutoring Interactions:</strong> Educational dialogues, comprehension questions, and practice responses submitted during structured learning sessions.</li>
          <li><strong>Audio & Voice Data (When Enabled):</strong> Temporary voice audio streams when using spoken tutoring features, processed exclusively for speech-to-text transcription. We do not use voice data for biometric identification or model training.</li>
          <li><strong>Technical & Security Data:</strong> IP address, device type, browser information, and access timestamps to ensure platform integrity and prevent unauthorized access.</li>
          <li><strong>Payment Processing:</strong> All subscription payments are processed securely by Stripe. We do not store credit card numbers or financial account secrets on our servers.</li>
        </ul>
      </section>

      <section>
        <h2>2. Children's Privacy & COPPA Compliance (Under 13)</h2>
        <p>
          The safety and privacy of young learners is paramount. Still Waters Academy complies fully with the <strong>Children's Online Privacy Protection Act (COPPA)</strong> and applicable global child data protection regulations.
        </p>
        <ul>
          <li><strong>Verifiable Parental Consent:</strong> Children under 13 cannot register independently. A child account can only be created and provisioned by an authenticated parent or legal guardian inside a paid family education facility. Creating a child profile constitutes verifiable parental consent for educational data processing.</li>
          <li><strong>Limited Data Collection:</strong> We collect only the student data reasonably necessary to deliver educational instruction, assess subject mastery, and generate parent reports.</li>
          <li><strong>No Commercial Exploitation or Advertising:</strong> We never sell children's personal information, never display third-party advertisements, and never build behavioral advertising profiles on students.</li>
          <li><strong>Parental Dashboard & Oversight:</strong> Parents have continuous access to their child's academic progress, enrolled courses, quiz outcomes, and AI evaluations through the secure Parent Dashboard.</li>
          <li><strong>Parental Rights (Review & Deletion):</strong> Parents and legal guardians have the right to review all data collected from their children, request corrections, refuse further collection, or demand the permanent deletion of their child's account and learning records at any time by contacting <a href="mailto:support@stillwatersacademy.com">support@stillwatersacademy.com</a>.</li>
        </ul>
      </section>

      <section>
        <h2>3. How We Use Educational & Account Data</h2>
        <p>
          We use information solely to support learner development and operational excellence:
        </p>
        <ul>
          <li><strong>Adaptive AI Tutoring:</strong> To tailor lesson explanations, pacing, and practice problems to the student's age bracket and historical mastery score.</li>
          <li><strong>Curriculum Reports & Certifications:</strong> To provide parents with comprehensive progress reports, strength summaries, and graduation certificates upon course completion.</li>
          <li><strong>Platform Security:</strong> To detect, investigate, and prevent abusive or malicious behavior, automated attacks, and security vulnerabilities.</li>
          <li><strong>Account Notifications:</strong> To send account confirmations, renewal notices, and critical security advisories.</li>
        </ul>
      </section>

      <section>
        <h2>4. Data Sharing & Service Providers</h2>
        <p>
          We do not sell, rent, or trade personal data. Information is shared only with trusted infrastructure providers governed by strict confidentiality and data-processing covenants:
        </p>
        <ul>
          <li><strong>AI Inference Infrastructure:</strong> We utilize leading enterprise AI providers (such as OpenAI and Anthropic) under commercial API terms where customer inputs are not used to train foundation models.</li>
          <li><strong>Database & Cloud Hosting:</strong> Secure cloud infrastructure hosted with enterprise encryption at rest and in transit.</li>
          <li><strong>Payment Processing:</strong> Stripe handles billing and subscription lifecycle management under PCI-DSS Level 1 certification.</li>
          <li><strong>Legal & Safety Obligations:</strong> If required by a valid legal subpoena, court order, or to protect the safety and welfare of a child or user.</li>
        </ul>
      </section>

      <section>
        <h2>5. Data Retention & Deletion</h2>
        <p>
          We retain educational records as long as your family or individual account is active. When an account is terminated:
        </p>
        <ul>
          <li><strong>Data Deletion:</strong> You can request immediate deletion of your entire account, including all student profiles and historical lesson logs, via the Settings portal or by contacting support.</li>
          <li><strong>Data Portability:</strong> Parents and solo learners may request an export of their academic records and course completion history at any time.</li>
        </ul>
      </section>

      <section>
        <h2>6. Security Measures</h2>
        <p>
          We employ robust technical, administrative, and physical safeguards. All platform traffic is encrypted using TLS 1.3, databases feature column-level encryption and row-level security (RLS), and administrative access is protected with multi-factor authentication.
        </p>
      </section>

      <section>
        <h2>7. Contact Us</h2>
        <p>
          For questions, data access requests, or COPPA inquiries, please reach out to our privacy administration:
        </p>
        <p>
          <strong>Email:</strong> <a href="mailto:support@stillwatersacademy.com">support@stillwatersacademy.com</a><br />
          <strong>Entity:</strong> Still Waters LLC (Wyoming, USA) dba Still Waters Academy<br />
          <strong>Address:</strong> 30 N Gould St Ste R, Sheridan, WY 82801, USA
        </p>
      </section>
    </LegalPageLayout>
  )
}
